/** Text Agent monitor — reads v1's AGENT state (rec_org_settings.AGENT, kept
 * fresh by the cron's agent_runner). Pure narrows + the send-window math ported
 * verbatim from buyers-agent.js. Approving here ("Send all approved") and the
 * repAuto / convAgent / autoOutreach toggles DO trigger sends in the cron. */

export interface AgentItem {
  address: string;
  num: string;
  status: string; // drafting | pending | scheduled | sent | error
  draft: string;
  kind: string; // opener | newhit | cut | week4 | dom60 | dom90 | bom | resubmit | gatekeeper | converse | pricedrop
  camp: string;
  added: number;
  sendAt: number | null;
  sentAt: number | null;
  err: string | null;
  /** cron-side reason a pending item was parked (e.g. "lead is Dead Deal, approval") */
  note: string | null;
  /** "dead_deal": the cron parked this reply because the lead is Dead Deal —
   *  shown on the page and skipped by "Send all approved" (2026-10-01) */
  flag: string | null;
}

export interface AgentVariantStat {
  id: string;
  camp: string;
  text: string;
  approved: boolean;
  sent: number;
  replies: number;
  pos: number;
  neg: number;
}

export interface AgentRepAuto { on: boolean; text: string }
export interface AgentConvAgent { on: boolean; auto: { needs_info: boolean; interested: boolean; qualified: boolean } }

/** v1 AGENT_REP_DEFAULT — the seed "do you have an agent?" auto-reply. */
export const AGENT_REP_DEFAULT = "Not yet — and honestly I'd love to represent myself on this one. If it needs work, show me before it lists; I'm a cash buyer and can close fast.";

export interface AgentState {
  queue: AgentItem[];
  variants: AgentVariantStat[];
  gkSent: number;
  gkReplies: number;
  gkOpps: number;
  textedCount: number;
  repAuto: AgentRepAuto;
  convAgent: AgentConvAgent;
  /** re-engage sweep toggle (cron drafts 48h+ unreached / still-on-market) */
  reengageOn: boolean;
  autoOutreachOn: boolean;
}

/** v1 DE_TRIG_LABEL + agent kinds. */
export const AGENT_KIND_LABEL: Record<string, string> = {
  opener: "opener", newhit: "new keyword hit", week4: "week-4 window", cut: "price cut",
  dom60: "60 days on market", dom90: "90 days on market", bom: "back on market",
  resubmit: "weekly offer re-submit", gatekeeper: "gatekeeper", converse: "conversation",
  pricedrop: "price-drop follow-up",
};

const s = (v: unknown): string => (typeof v === "string" ? v : "");
const n = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

export function narrowAgent(value: unknown): AgentState {
  const empty: AgentState = { queue: [], variants: [], gkSent: 0, gkReplies: 0, gkOpps: 0, textedCount: 0, repAuto: { on: false, text: AGENT_REP_DEFAULT }, convAgent: { on: false, auto: { needs_info: false, interested: false, qualified: false } }, reengageOn: false, autoOutreachOn: false };
  if (value === null || typeof value !== "object" || Array.isArray(value)) return empty;
  const a = value as Record<string, unknown>;
  const queueRaw = Array.isArray(a["queue"]) ? a["queue"] : [];
  const queue: AgentItem[] = [];
  for (const q of queueRaw) {
    if (q === null || typeof q !== "object" || Array.isArray(q)) continue;
    const r = q as Record<string, unknown>;
    queue.push({
      address: s(r["a"]),
      num: s(r["num"]),
      status: s(r["status"]) || "pending",
      draft: s(r["draft"]),
      kind: s(r["kind"]) || "opener",
      camp: s(r["camp"]) || "default",
      added: n(r["added"]),
      sendAt: n(r["sendAt"]) || null,
      sentAt: n(r["sentAt"]) || null,
      err: s(r["err"]) || null,
      note: s(r["note"]) || null,
      flag: s(r["flag"]) || null,
    });
  }
  const vstats = (a["vstats"] ?? {}) as Record<string, unknown>;
  const variantsRaw = Array.isArray(a["variants"]) ? a["variants"] : [];
  const variants: AgentVariantStat[] = [];
  for (const v of variantsRaw) {
    if (v === null || typeof v !== "object" || Array.isArray(v)) continue;
    const r = v as Record<string, unknown>;
    const id = s(r["id"]);
    const st = (vstats[id] ?? {}) as Record<string, unknown>;
    variants.push({
      id,
      camp: s(r["camp"]) || "default",
      text: s(r["text"] ?? r["body"] ?? r["t"]),
      approved: r["approved"] === true,
      sent: n(st["sent"]),
      replies: n(st["replies"]),
      pos: n(st["pos"]),
      neg: n(st["neg"]),
    });
  }
  const gk = (a["gk"] ?? {}) as Record<string, unknown>;
  const texted = a["textedNums"];
  const repRaw = (a["repAuto"] ?? {}) as Record<string, unknown>;
  const convRaw = (a["convAgent"] ?? {}) as Record<string, unknown>;
  const convAutoRaw = (convRaw["auto"] ?? {}) as Record<string, unknown>;
  return {
    queue,
    variants,
    gkSent: n(gk["sent"]),
    gkReplies: n(gk["replies"]),
    gkOpps: n(gk["opps"]),
    textedCount: texted !== null && typeof texted === "object" && !Array.isArray(texted) ? Object.keys(texted as object).length : 0,
    repAuto: { on: repRaw["on"] === true, text: s(repRaw["text"]) || AGENT_REP_DEFAULT },
    convAgent: { on: convRaw["on"] === true, auto: { needs_info: convAutoRaw["needs_info"] === true, interested: convAutoRaw["interested"] === true, qualified: convAutoRaw["qualified"] === true } },
    reengageOn: (a["reengage"] as Record<string, unknown> | undefined)?.["on"] === true,
    autoOutreachOn: (a["autoOutreach"] as Record<string, unknown> | undefined)?.["on"] === true,
  };
}

/** v1 _agentETHour: hour-of-day in America/New_York as a float (DST-safe). */
export function agentETHour(ts: number): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", hour12: false, hour: "2-digit", minute: "2-digit",
    }).formatToParts(new Date(ts));
    let h = +(parts.find((x) => x.type === "hour")?.value ?? 12);
    if (h === 24) h = 0;
    return h + +(parts.find((x) => x.type === "minute")?.value ?? 0) / 60;
  } catch {
    return 12; // fail-open like v1 (assume daytime)
  }
}

/** v1 agentInSendWindow: 9 AM – 8 PM Eastern. */
export const agentInSendWindow = (ts: number): boolean => {
  const h = agentETHour(ts);
  return h >= 9 && h < 20;
};

export interface QueueSummary {
  sent: number;
  pending: number;
  /** of `pending`: parked by the cron (flag set) — not covered by "Send all approved" */
  held: number;
  scheduled: number;
  drafting: number;
  error: number;
}

export function agentQueueSummary(queue: AgentItem[]): QueueSummary {
  const out: QueueSummary = { sent: 0, pending: 0, scheduled: 0, drafting: 0, error: 0, held: 0 };
  for (const q of queue) {
    if (q.status === "sent") out.sent++;
    else if (q.status === "scheduled") out.scheduled++;
    else if (q.status === "drafting") out.drafting++;
    else if (q.status === "error") out.error++;
    else {
      out.pending++;
      if (q.flag) out.held++;          // pending but parked by the cron (Dead Deal)
    }
  }
  return out;
}

// ---- Phase 13: writes (drafts / skips / script approvals) --------------------
// v1's Text Agent state is ONE org-settings value; v1 whole-writes it on every
// change (persistState). v2 does the same through the door — settings put,
// k='AGENT' — but always mutates a FRESH copy surgically, preserving every
// key it doesn't understand. SENDING stays impossible until Phase B.
import type { RecOp } from "../../lib/outbox";
import { http } from "../../lib/http";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";

/** identity of a queue item across copies — v1 mutates by index in ITS copy;
 * cross-app that's unsafe, so v2 matches by the item's own fields. */
export interface AgentItemId {
  a: string;
  num: string;
  kind: string;
  added: number;
}

export const agentItemId = (q: Pick<AgentItem, "address" | "num" | "kind" | "added">): AgentItemId => ({
  a: q.address,
  num: q.num,
  kind: q.kind,
  added: q.added,
});

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const matchesId = (rq: unknown, id: AgentItemId): boolean =>
  isObj(rq) &&
  s(rq["a"]) === id.a &&
  s(rq["num"]) === id.num &&
  s(rq["kind"]) === id.kind &&
  n(rq["added"]) === id.added;

/** Fresh AGENT value straight from rec_org_settings — the read half of every
 * read-modify-write (narrowest possible race window; v1 writes from a copy
 * that can be minutes stale). */
export async function fetchAgentFresh(token: string | null): Promise<Record<string, unknown> | null> {
  if (!token) return null;
  try {
    const rows = await http<{ value?: unknown }[]>(
      `${SUPABASE_URL}/rest/v1/rec_org_settings?nat_key=eq.AGENT&select=value&limit=1`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, timeoutMs: 15000 },
    );
    const v = rows?.[0]?.value;
    return isObj(v) ? v : null;
  } catch {
    return null;
  }
}

/** v1 agentEditDraft: queue[i].draft = val — here matched by identity.
 * Returns the new blob, or null if the item is gone (changed in v1). */
export function agentEditDraftIn(blob: unknown, id: AgentItemId, draft: string): Record<string, unknown> | null {
  if (!isObj(blob) || !Array.isArray(blob["queue"])) return null;
  const ix = blob["queue"].findIndex((rq) => matchesId(rq, id));
  if (ix < 0) return null;
  const queue = [...(blob["queue"] as unknown[])];
  queue[ix] = { ...(queue[ix] as Record<string, unknown>), draft };
  return { ...blob, queue };
}

/** v1 convSkip / queue removal: splice the matched item out. */
export function agentSkipItemIn(blob: unknown, id: AgentItemId): Record<string, unknown> | null {
  if (!isObj(blob) || !Array.isArray(blob["queue"])) return null;
  const ix = blob["queue"].findIndex((rq) => matchesId(rq, id));
  if (ix < 0) return null;
  const queue = (blob["queue"] as unknown[]).filter((_, i) => i !== ix);
  return { ...blob, queue };
}

/** A/B script approve/pause: variants[id].approved = flag. */
export function agentToggleVariantIn(blob: unknown, variantId: string, approved: boolean): Record<string, unknown> | null {
  if (!variantId || !isObj(blob) || !Array.isArray(blob["variants"])) return null;
  const ix = blob["variants"].findIndex((rv) => isObj(rv) && s(rv["id"]) === variantId);
  if (ix < 0) return null;
  const variants = [...(blob["variants"] as unknown[])];
  variants[ix] = { ...(variants[ix] as Record<string, unknown>), approved };
  return { ...blob, variants };
}

/** v1 agentVarSet(i,'text'|'camp'): edit an opener variant's body/campaign,
 * matched by id in a FRESH copy (v1 mutates by index in its own copy). */
export function agentEditVariantIn(blob: unknown, variantId: string, patch: { text?: string; camp?: string }): Record<string, unknown> | null {
  if (!variantId || !isObj(blob) || !Array.isArray(blob["variants"])) return null;
  const ix = blob["variants"].findIndex((rv) => isObj(rv) && s(rv["id"]) === variantId);
  if (ix < 0) return null;
  const variants = [...(blob["variants"] as unknown[])];
  const next = { ...(variants[ix] as Record<string, unknown>) };
  if (patch.text !== undefined) next["text"] = patch.text;
  if (patch.camp !== undefined) next["camp"] = patch.camp;
  variants[ix] = next;
  return { ...blob, variants };
}

/** v1 agentGenVariants push / manual add: append a fresh opener variant. */
export function agentAddVariantIn(blob: unknown, variant: { id: string; text: string; camp: string; approved: boolean }): Record<string, unknown> | null {
  if (!isObj(blob)) return null;
  const variants = Array.isArray(blob["variants"]) ? [...(blob["variants"] as unknown[])] : [];
  variants.push({ id: variant.id, text: variant.text, camp: variant.camp, approved: variant.approved });
  return { ...blob, variants };
}

/** v1 agentVarDel: drop a variant by id. */
export function agentDelVariantIn(blob: unknown, variantId: string): Record<string, unknown> | null {
  if (!variantId || !isObj(blob) || !Array.isArray(blob["variants"])) return null;
  const ix = blob["variants"].findIndex((rv) => isObj(rv) && s(rv["id"]) === variantId);
  if (ix < 0) return null;
  const variants = (blob["variants"] as unknown[]).filter((_, i) => i !== ix);
  return { ...blob, variants };
}

/** Fresh variant id — v1 backfills `v`+random hex (buyers-agent.js:163); here
 * time + a session counter so a burst of adds in the same ms stay unique. */
let _agentVarSeq = 0;
export const agentVariantId = (): string => "v" + Date.now().toString(36) + (_agentVarSeq++).toString(36);

/** v1 agentRepSet: toggle / edit the "do you have an agent?" auto-reply. */
export function agentSetRepAutoIn(blob: unknown, patch: { on?: boolean; text?: string }): Record<string, unknown> | null {
  if (!isObj(blob)) return null;
  const cur = (isObj(blob["repAuto"]) ? blob["repAuto"] : {}) as Record<string, unknown>;
  return { ...blob, repAuto: { ...cur, ...patch } };
}

/** Re-engage sweep toggle (Tom 2026-08-07): the cron drafts openers for 48h+
 * never-reached Zillow leads and follow-ups for reached-but-still-on-market
 * ones. Drafts only — sending still takes the approve button. */
export function agentSetReengageIn(blob: unknown, on: boolean): Record<string, unknown> | null {
  if (!isObj(blob)) return null;
  const cur = (isObj(blob["reengage"]) ? blob["reengage"] : {}) as Record<string, unknown>;
  return { ...blob, reengage: { ...cur, on, at: Date.now() } };
}

/** Auto-outreach master switch (Tom 2026-08-11: "start the auto ai to text
 * all the leads that are relevant automatically"). ON -> the cron sends cold
 * intro drafts ITSELF, under every existing gate (Zillow-only fence, 50/day,
 * re-intro anti-spam, 9-8 ET quiet hours). OFF -> intros wait for the approve
 * tap exactly as before. This flips a flag; the cron does the sending. */
export function agentSetAutoOutreachIn(blob: unknown, on: boolean): Record<string, unknown> | null {
  if (!isObj(blob)) return null;
  const cur = (isObj(blob["autoOutreach"]) ? blob["autoOutreach"] : {}) as Record<string, unknown>;
  return { ...blob, autoOutreach: { ...cur, on, at: Date.now() } };
}

/** "Send all approved" (2026-08-07, live): MARK every pending item approved —
 * the cron does the sending through its gates. This function sends nothing. */
/** Approve ONE pending item (used for items the bulk button skips, e.g. a reply
 *  the cron parked because the lead is Dead Deal — approving it by hand is
 *  Tom's explicit override, not automation). */
export function agentApproveItemIn(blob: unknown, id: AgentItemId): Record<string, unknown> | null {
  if (!isObj(blob) || !Array.isArray(blob["queue"])) return null;
  const ix = blob["queue"].findIndex((rq) => matchesId(rq, id));
  if (ix < 0) return null;
  const cur = blob["queue"][ix];
  if (!isObj(cur) || cur["status"] !== "pending" || !s(cur["draft"]).trim()) return null;
  // deadOverride: this tap, on a parked (Dead Deal) item, is Tom's explicit
  // override — the cron sends it without re-running the Dead Deal gate. A
  // bulk approve never sets it, so the gate still parks those.
  const override = cur["flag"] === "dead_deal" ? { deadOverride: true } : {};
  const queue = (blob["queue"] as unknown[]).map((x, i) =>
    i === ix ? { ...(x as Record<string, unknown>), ...override, status: "approved", approvedAt: Date.now() } : x);
  return { ...blob, queue };
}

export function agentApproveAllPendingIn(blob: unknown): Record<string, unknown> | null {
  if (!isObj(blob)) return null;
  const q = Array.isArray(blob["queue"]) ? (blob["queue"] as unknown[]) : [];
  return {
    ...blob,
    queue: q.map((x) => {
      if (x !== null && typeof x === "object" && !Array.isArray(x)
          && (x as Record<string, unknown>)["status"] === "pending"
          && !(x as Record<string, unknown>)["flag"]       // parked items need a deliberate tap
          && typeof (x as Record<string, unknown>)["draft"] === "string"
          && ((x as Record<string, unknown>)["draft"] as string).trim()) {
        return { ...(x as Record<string, unknown>), status: "approved", approvedAt: Date.now() };
      }
      return x;
    }),
  };
}

/** v1 agentConvSet: toggle the Conversation Agent + its per-category auto-send. */
export function agentSetConvIn(blob: unknown, patch: { on?: boolean; auto?: Partial<{ needs_info: boolean; interested: boolean; qualified: boolean }> }): Record<string, unknown> | null {
  if (!isObj(blob)) return null;
  const cur = (isObj(blob["convAgent"]) ? blob["convAgent"] : {}) as Record<string, unknown>;
  const curAuto = (isObj(cur["auto"]) ? cur["auto"] : {}) as Record<string, unknown>;
  const next: Record<string, unknown> = { ...cur };
  if (patch.on !== undefined) next["on"] = patch.on;
  if (patch.auto) next["auto"] = { ...curAuto, ...patch.auto };
  return { ...blob, convAgent: next };
}

/** The ONLY write shape: whole-AGENT put through the Phase 13 door section. */
export function opPutAgent(blob: Record<string, unknown>): RecOp {
  return { s: "settings", op: "put", k: "AGENT", d: blob };
}

export const AGENT_MSG = {
  gone: "That item changed in v1 just now — refreshed; try again.",
  sendPhaseB: "Sending unlocks in the live-fire session",
  // 2026-08-07: was "v1 picks it up on its next sync" — v1 is retired and
  // nothing syncs from it. The queue runner reads this same AGENT blob.
  saved: "Saved ✓",
};
